import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { IUser } from '../../interfaces/IUser';
import { ApiRequestError } from '../../services/ApiClientService';
import {
  Organization,
  createOrganization,
  getOrganization,
  getOrganizationByDomain,
  updateOrganization,
} from '../../services/organization.service';
import {
  deleteBilling,
  updateBillingMonthly,
  updateBillingOrg,
  updateBillingSixMonth,
  updatePayment,
} from '../../services/user.service';
import { useAuthStore } from '../../stores/authStore';
import { MainProfileLayout } from './MainProfileLayout';
import { StripeCardElement, StripeClient, StripePaymentFields } from './StripePaymentFields';

const getAccountTypeFromUser = (user: IUser | null): 'free' | 'gold' | 'gold-six-month' | 'org' => {
  if (user?.accountLevel === 1) {
    return 'gold';
  }

  if (user?.accountLevel === 2) {
    return 'gold-six-month';
  }

  if (user?.accountLevel === 3) {
    return 'org';
  }

  return 'free';
};

const getSubscriptionMessage = (user: IUser | null) => {
  if (!user) {
    return '';
  }

  if ((user.accountLevel === 1 || user.accountLevel === 2) && user.subscribedUntil) {
    const dateText = new Date(user.subscribedUntil).toDateString();
    if (user.willResubscribe) {
      return `Your gold subscription will refresh: ${dateText}`;
    }

    return `You have an active gold subscription until ${dateText}`;
  }

  if (user.accountLevel === 3) {
    return 'You are part of an organization subscription';
  }

  if (user.accountLevel === 99 && user.subscribedUntil) {
    return `You have an active gold subscription until ${new Date(
      user.subscribedUntil
    ).toDateString()}`;
  }

  return '';
};

const extractIntent = (error: ApiRequestError) => {
  if (
    error.data &&
    typeof error.data === 'object' &&
    'intent' in error.data &&
    typeof error.data.intent === 'string' &&
    error.data.intent.trim()
  ) {
    return error.data.intent;
  }

  const value =
    typeof error.data === 'string'
      ? error.data
      : typeof error.message === 'string'
      ? error.message
      : '';

  if (!value) {
    return null;
  }

  try {
    const parsed = JSON.parse(value) as { intent?: string };
    if (typeof parsed.intent === 'string' && parsed.intent.trim()) {
      return parsed.intent;
    }
  } catch {
    return null;
  }

  return null;
};

export const BillingPage = () => {
  const { user, getMe, setMe } = useAuthStore();

  const [selectedAccountType, setSelectedAccountType] = useState(getAccountTypeFromUser(user));
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [orgName, setOrgName] = useState('');
  const [orgDomain, setOrgDomain] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [orgError, setOrgError] = useState('');
  const [requiresOrgConfirm, setRequiresOrgConfirm] = useState(false);
  const [showGoldPaymentDetails, setShowGoldPaymentDetails] = useState(false);
  const [showOrgPaymentDetails, setShowOrgPaymentDetails] = useState(false);

  const [stripeClient, setStripeClient] = useState<StripeClient | null>(null);
  const stripeCardElementRef = useRef<StripeCardElement | null>(null);
  const isGoldSelected = selectedAccountType === 'gold' || selectedAccountType === 'gold-six-month';
  const isOrgSelected = selectedAccountType === 'org';

  const subscriptionMessage = useMemo(() => getSubscriptionMessage(user), [user]);

  useEffect(() => {
    setSelectedAccountType(getAccountTypeFromUser(user));
  }, [user]);

  useEffect(() => {
    if (selectedAccountType === 'gold') {
      setShowGoldPaymentDetails((user?.accountLevel ?? 0) !== 1);
      setShowOrgPaymentDetails(false);
      return;
    }

    if (selectedAccountType === 'gold-six-month') {
      setShowGoldPaymentDetails((user?.accountLevel ?? 0) !== 2);
      setShowOrgPaymentDetails(false);
      return;
    }

    if (selectedAccountType === 'org') {
      setShowGoldPaymentDetails(false);
      setShowOrgPaymentDetails((user?.accountLevel ?? 0) !== 3);
      return;
    }

    setShowGoldPaymentDetails(false);
    setShowOrgPaymentDetails(false);
  }, [selectedAccountType, user?.accountLevel]);

  useEffect(() => {
    if (user?.accountLevel !== 3) {
      setOrganization(null);
      return;
    }

    let mounted = true;

    void getOrganization()
      .then((response) => {
        if (mounted) {
          setOrganization(response);
        }
      })
      .catch(() => {
        if (mounted) {
          setOrganization(null);
        }
      });

    return () => {
      mounted = false;
    };
  }, [user?.accountLevel]);

  const handleStripeReadyChange = useCallback(
    (client: StripeClient | null, card: StripeCardElement | null) => {
      setStripeClient(client);
      stripeCardElementRef.current = card;
    },
    []
  );

  const subscriptionComplete = async () => {
    const updatedUser = await getMe();
    await setMe(updatedUser);

    stripeCardElementRef.current?.clear();
    setSuccessMessage('Subscription successfully updated');
    setErrorMessage('');
    setOrgError('');
    setRequiresOrgConfirm(false);
  };

  const applyStrongCustomerAuth = async (intent: string) => {
    if (!stripeClient || !stripeCardElementRef.current) {
      throw new Error('Unable to load subscription payment');
    }

    const response = await stripeClient.confirmCardPayment(intent, {
      payment_method: {
        card: stripeCardElementRef.current,
      },
    });

    if (response.error || response.paymentIntent?.status !== 'succeeded') {
      throw new Error('Unable to complete the subscription payment');
    }
  };

  const completeBillingSideEffects = async (previousAccountLevel: number) => {
    if (selectedAccountType !== 'org') {
      return;
    }

    if (previousAccountLevel !== 3 || !organization?._id) {
      await createOrganization(orgName.trim(), orgDomain.trim());
    } else {
      await updateOrganization(organization._id);
    }
  };

  const submitSubscription = async (tokenId: string) => {
    const currentLevel = user?.accountLevel ?? 0;

    if (currentLevel === 0 || currentLevel === 99) {
      if (selectedAccountType === 'gold') {
        await updateBillingMonthly(tokenId);
      } else if (selectedAccountType === 'gold-six-month') {
        await updateBillingSixMonth(tokenId);
      } else if (selectedAccountType === 'org') {
        await updateBillingOrg(tokenId, orgDomain.trim());
        await createOrganization(orgName.trim(), orgDomain.trim());
      }

      return;
    }

    const accountLevel =
      selectedAccountType === 'gold-six-month' ? 2 : selectedAccountType === 'org' ? 3 : 1;

    await updatePayment(tokenId, accountLevel);

    if (accountLevel === 3) {
      if (currentLevel !== 3 || !organization?._id) {
        await createOrganization(orgName.trim(), orgDomain.trim());
      } else {
        await updateOrganization(organization._id);
      }
    }
  };

  const onSubmit = async () => {
    setErrorMessage('');
    setSuccessMessage('');
    setOrgError('');

    const currentAccountLevel = user?.accountLevel ?? 0;

    if (
      selectedAccountType === 'free' &&
      (currentAccountLevel === 1 || currentAccountLevel === 2 || currentAccountLevel === 3)
    ) {
      setIsSaving(true);
      try {
        await deleteBilling();
        await subscriptionComplete();
      } catch (error: any) {
        setErrorMessage(error?.message || 'Unable to update billing subscription');
      } finally {
        setIsSaving(false);
      }
      return;
    }

    if (selectedAccountType === 'org' && currentAccountLevel !== 3) {
      if (!orgName.trim()) {
        setOrgError('A name for the organization is required');
        return;
      }

      if (!orgDomain.trim()) {
        setOrgError('A domain for the organization is required');
        return;
      }

      if (!orgDomain.includes('@')) {
        setOrgError('A valid email domain for the organization is required');
        return;
      }

      if (!requiresOrgConfirm) {
        try {
          const org = await getOrganizationByDomain(orgDomain.trim());
          if (org?.subscribedUntil) {
            setRequiresOrgConfirm(true);
            setOrgError(
              `There is a valid subscription for this domain until ${new Date(
                org.subscribedUntil
              ).toDateString()}. Continuing will start a new subscription.`
            );
            return;
          }
        } catch (error: any) {
          const apiError = error as ApiRequestError;
          if (apiError.status !== 404) {
            setErrorMessage(error?.message || 'Unable to validate organization domain');
            return;
          }
        }
      }
    }

    if (!stripeClient || !stripeCardElementRef.current) {
      setErrorMessage('Unable to load subscription payment');
      return;
    }

    setIsSaving(true);

    try {
      const stripeResponse = await stripeClient.createToken(stripeCardElementRef.current);
      if (stripeResponse.error?.message || !stripeResponse.token?.id) {
        const message = stripeResponse.error?.message || 'Unable to load subscription payment';
        if (selectedAccountType === 'org') {
          setOrgError(message);
        } else {
          setErrorMessage(message);
        }
        return;
      }

      await submitSubscription(stripeResponse.token.id);
      await subscriptionComplete();
    } catch (error: any) {
      const apiError = error as ApiRequestError;
      if (apiError.status === 403) {
        const intent = extractIntent(apiError);
        if (intent) {
          try {
            await applyStrongCustomerAuth(intent);
            await completeBillingSideEffects(currentAccountLevel);
            await subscriptionComplete();
            return;
          } catch (authError: any) {
            const message = authError?.message || 'Unable to complete the subscription payment';
            if (selectedAccountType === 'org') {
              setOrgError(message);
            } else {
              setErrorMessage(message);
            }
            return;
          }
        }
      }

      const message = error?.message || 'Unable to update billing subscription';
      if (selectedAccountType === 'org') {
        setOrgError(message);
      } else {
        setErrorMessage(message);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const currentAccountLevel = user?.accountLevel ?? 0;
  const isReadOnlyOrgMember =
    currentAccountLevel === 3 && organization !== null && organization.isOwner === false;
  const showGoldExplain = selectedAccountType === 'free' || selectedAccountType === 'org';
  const showGoldDetail = isGoldSelected;
  const showGoldUpdateLink =
    (selectedAccountType === 'gold' && currentAccountLevel === 1 && !showGoldPaymentDetails) ||
    (selectedAccountType === 'gold-six-month' &&
      currentAccountLevel === 2 &&
      !showGoldPaymentDetails);
  const showOrgExplain = selectedAccountType !== 'org';
  const showOrgDetail = isOrgSelected;
  const showOrgUpdateLink =
    selectedAccountType === 'org' && currentAccountLevel === 3 && !showOrgPaymentDetails;
  const showOrgReadOnlyDetails = currentAccountLevel === 3 && Boolean(organization);

  const sectionHeaderSx = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 2,
    fontWeight: 700,
    color: '#022b3a',
  } as const;

  const radioGroupSx = {
    alignItems: 'flex-end',
    '& .MuiFormControlLabel-root': {
      justifyContent: 'flex-end',
      mr: 0,
      ml: 0,
      textAlign: 'right',
    },
    '& .MuiFormControlLabel-label': {
      textAlign: 'right',
    },
  } as const;

  return (
    <MainProfileLayout section="billing" headerLabel="Billing">
      <Stack spacing={2} sx={{ maxWidth: 840 }}>
        {subscriptionMessage ? (
          <Alert
            severity="info"
            icon={false}
            sx={{
              bgcolor: '#ffd04b',
              color: '#ffffff',
              fontWeight: 700,
              '& .MuiAlert-message': {
                width: '100%',
              },
            }}
          >
            {subscriptionMessage}
          </Alert>
        ) : null}
        {successMessage ? <Alert severity="success">{successMessage}</Alert> : null}

        {isReadOnlyOrgMember ? (
          <Stack spacing={2}>
            <Typography sx={sectionHeaderSx}>Organization / School Plan</Typography>
            <Box sx={{ border: '1px solid #e7e7e7', bgcolor: '#ffffff' }}>
              <Stack divider={<Box sx={{ borderTop: '1px solid #e7e7e7' }} />}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '155px 1fr', minHeight: 48 }}>
                  <Box sx={{ p: 1.5, fontWeight: 600, color: '#022b3a' }}>Name</Box>
                  <Box sx={{ p: 1.5 }}>{organization.name || '-'}</Box>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: '155px 1fr', minHeight: 48 }}>
                  <Box sx={{ p: 1.5, fontWeight: 600, color: '#022b3a' }}>Owner</Box>
                  <Box sx={{ p: 1.5 }}>{organization.ownerName || '-'}</Box>
                </Box>
              </Stack>
            </Box>
          </Stack>
        ) : (
          <Box sx={{ bgcolor: '#ffffff', border: '1px solid #e7e7e7', p: { xs: 2, md: 3 } }}>
            <Stack spacing={2.5}>
              <Box>
                <Box sx={sectionHeaderSx}>
                  <Typography sx={{ fontWeight: 700, color: '#022b3a' }}>
                    Standard Account
                  </Typography>
                  <Box>
                    <RadioGroup
                      value={selectedAccountType}
                      sx={radioGroupSx}
                      onChange={(event) => {
                        setSelectedAccountType(event.target.value as typeof selectedAccountType);
                        setRequiresOrgConfirm(false);
                        setOrgError('');
                      }}
                    >
                      <FormControlLabel
                        value="free"
                        control={<Radio size="small" />}
                        label="Free"
                      />
                    </RadioGroup>
                  </Box>
                </Box>
                <Typography sx={{ mt: 1, color: '#022b3a' }}>
                  All the great standard features of dotstorming but only 1 space
                </Typography>
              </Box>

              <Box>
                <Box sx={sectionHeaderSx}>
                  <Typography sx={{ fontWeight: 700, color: '#022b3a' }}>
                    Gold Subscription
                  </Typography>
                  <RadioGroup
                    value={selectedAccountType}
                    sx={radioGroupSx}
                    onChange={(event) => {
                      setSelectedAccountType(event.target.value as typeof selectedAccountType);
                      setRequiresOrgConfirm(false);
                      setOrgError('');
                    }}
                  >
                    <FormControlLabel
                      value="gold"
                      control={<Radio size="small" />}
                      label="$5 per month"
                    />
                    <FormControlLabel
                      value="gold-six-month"
                      control={<Radio size="small" />}
                      label="$25 per 6 months"
                    />
                  </RadioGroup>
                </Box>

                {showGoldExplain ? (
                  <Box sx={{ mt: 1 }}>
                    <Typography sx={{ color: '#022b3a', mb: 1 }}>
                      All the great standard features of dotstorming AND
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 3, color: '#022b3a' }}>
                      <Box component="li">
                        No space limits - Create an unlimited number of spaces
                      </Box>
                      <Box component="li">
                        Video and File uploads - Share video and files with participants
                      </Box>
                      <Box component="li">Passwords - Lock down your space</Box>
                      <Box component="li">
                        Imports - The ability to import from external sources into dotstorming
                      </Box>
                      <Box component="li">
                        Theming - Remove dotstorming branding and add your own
                      </Box>
                      <Box component="li">Support and a voice in the direction of dotstorming</Box>
                    </Box>
                  </Box>
                ) : null}

                {showGoldDetail ? (
                  <Box sx={{ mt: 2 }}>
                    {showGoldUpdateLink ? (
                      <Typography
                        onClick={() => setShowGoldPaymentDetails(true)}
                        sx={{
                          color: '#625dcf',
                          cursor: 'pointer',
                          fontWeight: 600,
                          display: 'inline-block',
                          mb: 1.5,
                        }}
                      >
                        Update your payment details
                      </Typography>
                    ) : null}

                    {showGoldPaymentDetails ? (
                      <Stack spacing={0} sx={{ borderTop: '2px solid #f1f1f1', pt: 2 }}>
                        {isGoldSelected && errorMessage ? (
                          <Alert severity="error" sx={{ mb: 2 }}>
                            {errorMessage}
                          </Alert>
                        ) : null}
                        <StripePaymentFields
                          visible={showGoldPaymentDetails}
                          onReadyChange={handleStripeReadyChange}
                        />
                      </Stack>
                    ) : null}
                  </Box>
                ) : null}
              </Box>

              <Box>
                <Box sx={sectionHeaderSx}>
                  <Typography sx={{ fontWeight: 700, color: '#022b3a' }}>
                    Organization / School Plan
                  </Typography>
                  <RadioGroup
                    value={selectedAccountType}
                    sx={radioGroupSx}
                    onChange={(event) => {
                      setSelectedAccountType(event.target.value as typeof selectedAccountType);
                      setRequiresOrgConfirm(false);
                      setOrgError('');
                    }}
                  >
                    <FormControlLabel
                      value="org"
                      control={<Radio size="small" />}
                      label="$250 per year"
                    />
                  </RadioGroup>
                </Box>

                {showOrgExplain ? (
                  <Box sx={{ mt: 1 }}>
                    <Typography sx={{ color: '#022b3a', mb: 1 }}>
                      All the great features of a Gold Subscription AND
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 3, color: '#022b3a' }}>
                      <Box component="li">
                        Anyone with your organization/school's email, e.g. @dotstorming.com,
                        automatically have the features of a Gold Subscription.
                      </Box>
                    </Box>
                  </Box>
                ) : null}

                {showOrgDetail ? (
                  <Box sx={{ mt: 2 }}>
                    {showOrgUpdateLink ? (
                      <Typography
                        onClick={() => setShowOrgPaymentDetails(true)}
                        sx={{
                          color: '#625dcf',
                          cursor: 'pointer',
                          fontWeight: 600,
                          display: 'inline-block',
                          mb: 1.5,
                        }}
                      >
                        Update your payment details
                      </Typography>
                    ) : null}

                    <Stack spacing={2} sx={{ borderTop: '2px solid #f1f1f1', pt: 2 }}>
                      {orgError ? <Alert severity="error">{orgError}</Alert> : null}

                      <Typography sx={{ fontWeight: 700, color: '#022b3a' }}>Details</Typography>

                      {showOrgReadOnlyDetails ? (
                        <Box sx={{ border: '1px solid #e7e7e7' }}>
                          <Stack divider={<Box sx={{ borderTop: '1px solid #e7e7e7' }} />}>
                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: '155px 1fr' },
                              }}
                            >
                              <Box sx={{ p: 1.5, fontWeight: 600, color: '#022b3a' }}>Name</Box>
                              <Box sx={{ p: 1.5 }}>{organization?.name || '-'}</Box>
                            </Box>
                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: '155px 1fr' },
                              }}
                            >
                              <Box sx={{ p: 1.5, fontWeight: 600, color: '#022b3a' }}>
                                E-mail Domain
                              </Box>
                              <Box sx={{ p: 1.5 }}>{organization?.domain || '-'}</Box>
                            </Box>
                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: '155px 1fr' },
                              }}
                            >
                              <Box sx={{ p: 1.5, fontWeight: 600, color: '#022b3a' }}>
                                Subscribed Until
                              </Box>
                              <Box sx={{ p: 1.5 }}>
                                {organization?.subscribedUntil
                                  ? new Date(organization.subscribedUntil).toDateString()
                                  : '-'}
                              </Box>
                            </Box>
                          </Stack>
                        </Box>
                      ) : (
                        <Box sx={{ border: '1px solid #e7e7e7' }}>
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: { xs: '1fr', sm: '155px 1fr' },
                              borderBottom: '1px solid #e7e7e7',
                            }}
                          >
                            <Box sx={{ p: 1.5, fontWeight: 600, color: '#022b3a' }}>Name</Box>
                            <Box sx={{ p: 1.5 }}>
                              <TextField
                                value={orgName}
                                onChange={(event) => setOrgName(event.target.value)}
                                fullWidth
                                size="small"
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter') {
                                    void onSubmit();
                                  }
                                }}
                              />
                            </Box>
                          </Box>
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: { xs: '1fr', sm: '155px 1fr' },
                            }}
                          >
                            <Box sx={{ p: 1.5, fontWeight: 600, color: '#022b3a' }}>
                              E-mail Domain
                            </Box>
                            <Box sx={{ p: 1.5 }}>
                              <TextField
                                value={orgDomain}
                                placeholder="e.g. @myorganizationURL.com"
                                onChange={(event) => setOrgDomain(event.target.value)}
                                fullWidth
                                size="small"
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter') {
                                    void onSubmit();
                                  }
                                }}
                              />
                            </Box>
                          </Box>
                        </Box>
                      )}

                      {showOrgPaymentDetails ? (
                        <StripePaymentFields
                          visible={showOrgPaymentDetails}
                          onReadyChange={handleStripeReadyChange}
                        />
                      ) : null}
                    </Stack>
                  </Box>
                ) : null}
              </Box>

              <Stack direction="row" justifyContent="flex-end">
                <Button variant="contained" onClick={onSubmit} disabled={isSaving}>
                  {isSaving
                    ? 'Updating...'
                    : requiresOrgConfirm
                    ? 'I understand, start the new subscription'
                    : 'Update'}
                </Button>
              </Stack>
            </Stack>
          </Box>
        )}
      </Stack>
    </MainProfileLayout>
  );
};
